<template>
	<div tabindex="0" class="hide-outline" style="margin-top: 58px">
		<ui-row v-if="error || isPending">
			<ui-col>
				<ui-alert v-if="error" error @click="error = ''">{{ error }}</ui-alert>
				<ui-progress v-if="isPending"/>
			</ui-col>
		</ui-row>
		<ui-row>
			<ui-col cols="12">
				<ui-card>
					<div
						class="level-up-container is-flex is-align-items-center mb-2"
						v-if="canLevelUp"
					>
						<i class="fas fa-level-up-alt fa-xs mr-1" @click="levelUp" />
						<p class="is-size-7 is-underlined" @click="levelUp">{{ $t('dashboard.level_up') }}</p>
					</div>

					<div class="is-flex is-align-items-center is-flex-wrap-wrap">
						<ui-title class="has-text-white mr-6" style="white-space: nowrap;">{{ getActiveCustomer.name }}</ui-title>

						<div class="is-flex is-flex-wrap-wrap">
							<p class="mr-4">{{ customerInformation?.departmentCount ?? 0 }} {{ $t('dashboard.departments') }}</p>
							<p class="mr-4">{{ customerInformation?.buildingCount ?? 0 }} {{ $t('dashboard.buildings') }}</p>
							<p class="mr-4">{{ customerInformation?.flawCount ?? 0 }} {{ $t('dashboard.flaws') }}</p>
						</div>
					</div>
				</ui-card>
			</ui-col>
		</ui-row>
		<ui-row>
			<ui-col cols="5" style="min-width: 250px; flex-grow: 1;">
				<ui-card>
					<ui-title class="has-text-white mb-2">{{ $t('dashboard.departments') }}</ui-title>
					<DashboardCustomers v-if="customerInformation?.departments?.length > 0" :customer-information="customerInformation"/>
					<p class="is-size-7" v-else>{{ $t('dashboard.no_departments') }}</p>

					<ui-title class="has-text-white mb-2 mr-2 mt-6">{{ $t('dashboard.buildings') }}</ui-title>
					<DashboardBuildings v-if="customerInformation?.buildings?.length > 0" :buildings="customerInformation.buildings"/>
					<p class="is-size-7" v-else>{{ $t('dashboard.no_buildings') }}</p>
				</ui-card>

				<ui-card class="mt-5" v-if="comExternalBuildings.length > 0">
					<ui-title class="has-text-white">{{ $t('dashboard.external_buildings') }}</ui-title>
					<DashboardBuildings :buildings="comExternalBuildings" />
				</ui-card>
			</ui-col>
			<ui-col cols="7" style="min-width: 250px; flex-grow: 1;">
				<div v-if="getActiveBuilding">
					<DashboardActions
						ref="dashboardActions"
						class="mb-4"
						:readonly="!$isAllowedTo('dashboard_perform_actions_on_content')"
						:customerId="comCustomerId"
						@rowClick="handleActionClick"
					/>

					<DashboardQuestions
						ref="dashboardQuestions"
						class="mb-4"
						:customerId="comCustomerId"
						@rowClick="handleQuestionRowClick"
					/>

					<DashboardPlannings :readonly="!$isAllowedTo('dashboard_perform_actions_on_content')" :customerId="comCustomerId"/>
				</div>
			</ui-col>
		</ui-row>

		<FlawModal
			v-if="flawModalVisable"
			:edit-flaw="selectedFlaw"
			@cancel="handleCloseFlawModal"
			@save="handleSaveFlaw"
		/>

		<QuestionModal
			v-if="questionModalVisible"
			:edit-question="selectedQuestion"
			@save="handleSaveQuestion"
			@cancel="handleCloseQuestionModal"
		/>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DashboardCustomers from '@/components/dashboard/customers';
import DashboardBuildings from '@/components/dashboard/buildings';
import DashboardActions from '@/components/dashboard/actions';
import DashboardQuestions from '@/components/dashboard/questions';
import DashboardPlannings from '@/components/dashboard/plannings';
import FlawModal from './FlawModal.vue';
import QuestionModal from './QuestionModal.vue';

export default {
	components: {
		DashboardCustomers,
		DashboardBuildings,
		DashboardActions,
		DashboardQuestions,
		DashboardPlannings,
		FlawModal,
		QuestionModal
	},

	data: () => ({
		customerInformation: null,
		parentInformation: null,
		externalBuildings: [],
		isPending: false,
		error: '',
		selectedFlaw: null,
		flawModalVisable: false,
		selectedQuestion: null,
		questionModalVisible: false
	}),

	computed: {
		...mapGetters('user', ['getUser', 'getActiveCustomer', 'getActiveCompany', 'getActiveBuilding']),
		canLevelUp() {
			return this.getActiveCustomer.parentId != null &&
				(this.$isAllowedTo('dashboard_back_past_own_root_node')
					? true
					: this.$isAllowedTo('tree_see_own_root')
						? this.getActiveCustomer?.parentId
						: this.getActiveCustomer.id !== this.getUser.customerId);
		},

		comExternalBuildings() {
			return this.externalBuildings.filter(eb => !this.customerInformation?.buildings?.some(b => eb.id === b.id));
		},

		comCustomerId() {
			if (this.comExternalBuildings.some(x => x.id === this.getActiveBuilding.id))
				return this.getActiveBuilding.customerId;

			return this.getActiveCustomer.id;
		}
	},

	watch: {
		async 'getActiveCustomer.id'() {
			await Promise.all([
				this.fetchCustomerInformation(),
				this.fetchParentCustomerInformation(),
				this.fetchExternalBuildings()]);

			if (this.getActiveBuilding != null && !this.customerInformation?.buildings?.some(x => x.id === this.getActiveBuilding?.id))
				this.setActiveBuilding(null);
		}
	},

	async created() {
		this.setActiveBuilding(null);

		await Promise.all([
			this.fetchCustomerInformation(),
			this.fetchParentCustomerInformation(),
			this.fetchExternalBuildings()]);
	},

	methods: {
		...mapActions('user', ['setActiveCustomer', 'setActiveBuilding']),

		async fetchCustomerInformation() {
			this.error = '';

			try {
				const data = await this.$services.customer.getCustomerInformation({
					customerId: this.getActiveCustomer.id,
					companyId: this.getActiveCustomer.companyId ?? this.getActiveCompany.id,
					userId: this.$isAllowedTo('dashboard_view_all_buildings') ? null : this.getUser.id
				}, {
					throwException: true,
					loading: val => {
						this.isPending = val;
					}
				});

				this.customerInformation = data;
			} catch (error) {
				this.error = error.message;
			}
		},

		async fetchParentCustomerInformation() {
			if (this.getActiveCustomer?.parentId == null) return;

			this.error = '';

			try {
				const data = await this.$services.customer.getCustomer(this.getActiveCustomer.parentId,
					{
						throwException: true,
						loading: val => {
							this.isPending = val;
						}
					});

				this.parentInformation = data;
			} catch (error) {
				this.error = error.message;
			}
		},

		async fetchExternalBuildings() {
			try {
				this.externalBuildings = await this.$services.customer.getExternalBuildings(this.getUser.id, {
					loading: val => this.isPending = val
				});
			} catch (error) {
				this.error = error.message;
			}
		},

		levelUp() {
			this.setActiveCustomer(this.parentInformation);
		},

		handleActionClick(item) {
			this.selectedFlaw = item;
			this.flawModalVisable = true;
		},

		handleCloseFlawModal() {
			this.flawModalVisable = false;
			this.selectedFlaw = null;
		},

		async handleSaveFlaw(flaw) {
			this.handleCloseFlawModal();
			this.isPending = true;

			try {
				await this.$services.flaw.updateFlaw(flaw);

				this.$refs?.dashboardActions?.fetchData();
			} catch (error) {
				console.error(error.message);
			}

			this.isPending = false;
		},

		async handleSaveQuestion(question) {
			this.handleCloseQuestionModal();
			this.isPending = true;

			try {
				await this.$services.roundHistoryQuestion.updateRoundHistoryQuestion(question);

				this.$refs?.dashboardQuestions?.fetchData();
			} catch (error) {
				console.error(error.message);
			}

			this.isPending = false;
		},

		handleCloseQuestionModal() {
			this.questionModalVisible = false;
			this.selectedQuestion = null;
		},

		handleQuestionRowClick(question) {
			this.selectedQuestion = question;
			this.questionModalVisible = true;
		}
	}
};
</script>

<style scoped lang="scss">
.level-up-container {
	.fa-level-up-alt {
		transform: rotateY(180deg);
	}

	&:hover {
		cursor: pointer;
	}
}
</style>
